import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { LoggerService } from 'app/core/logger/logger.service';
import { Navigation } from 'app/core/navigation/navigation.types';
import { Observable, ReplaySubject, tap, map, switchMap, of, BehaviorSubject } from 'rxjs';
import { UserService } from 'app/core/user/user.service';
import { hasRole } from 'app/core/user/user-role.utils';
import { User } from 'app/core/user/user.types';
import { StudentService } from 'app/core/services/student.service';
import { FuseNavigationItem } from '@fuse/components/navigation';
import {
    adminNavigation,
    studentNavigation,
    tutorNavigation,
    defaultNavigation
} from 'app/mock-api/common/navigation/data';
import { cloneDeep } from 'lodash-es';

@Injectable({ providedIn: 'root' })
export class NavigationService {
    private readonly _logger = inject(LoggerService);

    private _httpClient = inject(HttpClient);
    private _userService = inject(UserService);
    private _studentService = inject(StudentService);
    private _navigation: BehaviorSubject<Navigation> =
        new BehaviorSubject<Navigation>({
            compact: [],
            default: [],
            futuristic: [],
            horizontal: [],
        });


    constructor() {
        this._userService.user$.subscribe(
            (user) => {
                // Modificar la navegación según el rol del usuario
                if (user && hasRole(user, 'student')) {
                    // Para estudiantes, obtener cursos y crear navegación dinámica
                    this._studentService.getMyCourses().subscribe({
                        next: (courses) => {
                            const modifiedNavigation = this.getNavigationBasedOnRole(user, courses);
                            this._navigation.next(modifiedNavigation);
                        },
                        error: (error) => {
                            this._logger.error('Error loading courses for navigation:', error);
                            // En caso de error, usar navegación básica
                            const modifiedNavigation = this.getNavigationBasedOnRole(user);
                            this._navigation.next(modifiedNavigation);
                        }
                    });
                } else {
                    // Para otros roles, usar navegación estática
                    const modifiedNavigation = this.getNavigationBasedOnRole(user);
                    this._navigation.next(modifiedNavigation);
                }
                return;
            }
        );
    }
    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for navigation
     */
    get navigation$(): Observable<Navigation> {
        return this._navigation.asObservable();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Get all navigation data
     */
    get(): Observable<Navigation> {
        return of({
            compact: [],
            default: [],
            futuristic: [],
            horizontal: [],
        });
    }

    /**
     * Obtener la navegación basada en el rol del usuario
     * @param user Usuario actual
     * @param courses Cursos del estudiante (opcional, solo para estudiantes)
     */
    private getNavigationBasedOnRole(user: User, courses?: any[]): Navigation {
        // Clonar la navegación para no modificar la original
        const navigation = cloneDeep({
            compact: [],
            default: [],
            futuristic: [],
            horizontal: [],
        });
        let roleBasedNavigation;

        // Determinar la navegación basada en el rol
        if (user && user.roles && user.roles.length > 0) {
            // Verificar el tipo de perfil activo del usuario.
            // Issue #13: ya no hay grupo "Super Admin" aparte — sus ítems
            // viven en el menú de admin y no se duplica el de alumnos.
            if (hasRole(user, 'admin')) {
                roleBasedNavigation = cloneDeep(adminNavigation);
            } else if (user.roles.includes('student')) {
                roleBasedNavigation = cloneDeep(studentNavigation);
                
                // Si hay cursos, agregar items de navegación dinámicos
                if (courses && courses.length > 0) {
                    // Encontrar el grupo "Mis Cursos"
                    const coursesGroup = roleBasedNavigation.find(item => item.id === 'student-courses');
                    
                    if (coursesGroup && coursesGroup.children) {
                        // Agregar un separador después de "Todos mis cursos"
                        coursesGroup.children.push({
                            id: 'courses-separator',
                            type: 'divider'
                        } as FuseNavigationItem);
                        
                        // Agregar cada curso como un item de navegación
                        courses.forEach((assignment, index) => {
                            if (assignment.course) {
                                const courseItem: FuseNavigationItem = {
                                    id: `course-${assignment.course.id}`,
                                    title: this.truncateCourseName(assignment.course.name),
                                    type: 'basic',
                                    icon: 'heroicons_outline:academic-cap',
                                    link: `/student/courses/${assignment.id}`
                                };
                                
                                // Agregar badge según el estado del curso
                                if (assignment.status) {
                                    const statusConfig = this.getStatusBadgeConfig(assignment.status);
                                    if (statusConfig) {
                                        courseItem.badge = statusConfig;
                                    }
                                }
                                
                                // Agregar tooltip con el nombre completo si fue truncado
                                if (assignment.course.name.length > 25) {
                                    courseItem.tooltip = assignment.course.name;
                                }
                                
                                coursesGroup.children.push(courseItem);
                            }
                        });
                    }
                }
            } else if (user.roles.includes('parent')) {
                roleBasedNavigation = cloneDeep(tutorNavigation);
            } else {
                // Perfil desconocido, usar navegación predeterminada
                roleBasedNavigation = cloneDeep(defaultNavigation);
            }
        } else {
            // Si no hay usuario o perfil activo, usar navegación predeterminada
            roleBasedNavigation = cloneDeep(defaultNavigation);
        }

        // Reemplazar la navegación por defecto con la navegación basada en rol
        navigation.compact = roleBasedNavigation;
        navigation.default = roleBasedNavigation;
        navigation.futuristic = roleBasedNavigation;
        navigation.horizontal = roleBasedNavigation;

        return navigation;
    }
    
    /**
     * Truncate course name for navigation display
     * @param name Course name to truncate
     * @returns Truncated name if longer than max length
     */
    private truncateCourseName(name: string): string {
        const maxLength = 18;
        if (name.length <= maxLength) {
            return name;
        }
        return name.substring(0, maxLength - 3) + '...';
    }
    
    /**
     * Get badge configuration based on course status
     * @param status Course status
     * @returns Badge configuration object or null
     */
    private getStatusBadgeConfig(status: string): any {
        const statusConfigs = {
            'pending': {
                title: 'Pendiente',
                classes: 'px-2 bg-orange-500 text-white rounded-full text-xs'
            },
            'active': null, // No badge for active courses
            'completed': {
                title: 'Completado',
                classes: 'px-2 bg-green-500 text-white rounded-full text-xs'
            },
            'suspended': {
                title: 'Suspendido',
                classes: 'px-2 bg-red-500 text-white rounded-full text-xs'
            }
        };
        
        return statusConfigs[status] || null;
    }
}
